import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, LogIn } from 'lucide-react';
import api from '../lib/api';
import FounderBadge from '../components/FounderBadge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

/**
 * Founders — public page listing every traveler who has opted in to appear
 * on the Founder Wall. This is the "View All" destination from the Landing
 * page marquee.
 *
 * Fetches in one shot (up to 200 rows) because /api/founders enforces its
 * own hard cap and the total user base at Early Access stage never exceeds
 * that. Client-side search filters by displayName / country.
 */
const PAGE_SIZE = 60;
const HARD_LIMIT = 200;

function Card({ user }) {
  const initials = (user.displayName || user.username || 'FV').slice(0, 2).toUpperCase();
  return (
    <div
      className="rounded-2xl bg-white/5 border border-white/10 hover:border-amber-400/40 backdrop-blur-sm p-4 flex flex-col items-center transition"
      data-testid={`founders-page-card-${user.user_id}`}
    >
      <div className="relative">
        <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-fuchsia-500/40 to-cyan-500/40 border-2 border-white/15 flex items-center justify-center">
          {user.avatar
            ? <img src={user.avatar} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            : <span className="text-xl font-bold text-white/90">{initials}</span>}
        </div>
        <FounderBadge size="sm" className="absolute -bottom-1 -right-1" />
      </div>
      <div className="mt-3 font-semibold text-white truncate w-full text-center">{user.displayName || user.username}</div>
      <div className="text-[11px] text-white/50 flex items-center gap-1 mt-0.5">
        <span>{user.flag || '🌍'}</span>
        <span className="uppercase tracking-wider">Rank {user.rank || 1}</span>
      </div>
    </div>
  );
}

export default function Founders() {
  const [data, setData] = useState({ total: 0, founders: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE_SIZE);

  useEffect(() => {
    let alive = true;
    api.get(`/founders?limit=${HARD_LIMIT}`)
      .then(r => { if (alive) setData(r.data || { total: 0, founders: [] }); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Client-side search across displayName / username / country. Cheap
  // because the fetched set is small (<=200 rows). If we ever exceed
  // HARD_LIMIT the endpoint itself caps at 200 and we'd need to add a
  // search=... query param server-side.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.founders;
    return data.founders.filter(u =>
      (u.displayName || '').toLowerCase().includes(q)
      || (u.username || '').toLowerCase().includes(q)
      || (u.country || '').toLowerCase().includes(q)
    );
  }, [data.founders, query]);

  const visible = filtered.slice(0, shown);
  const hasMore = filtered.length > shown;

  return (
    <div className="min-h-screen bg-[#0A0A14] text-white pb-24">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/40 border-b border-white/8">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="founders-back">
            <ArrowLeft size={16} /> Back to landing
          </Link>
          <Link to="/login" className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-black text-xs font-semibold hover:opacity-90" data-testid="founders-signin-cta">
            <LogIn size={14} /> Sign in
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-6 text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-3 flex items-center justify-center gap-2">
          <FounderBadge size="sm" /> The Founder Wall
        </div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-fuchsia-300 to-cyan-300">
            {loading ? '…' : data.total.toLocaleString()}
          </span>{' '}
          <span className="text-white">launch travelers</span>
        </h1>
        <p className="mt-3 text-sm sm:text-base text-white/60 max-w-2xl mx-auto">
          Every traveler here explicitly opted in to appear on the public wall. They&apos;ll wear
          the permanent <span className="text-amber-300 font-semibold">Founder Badge</span> on their profile forever.
        </p>

        {/* Search */}
        <div className="mt-6 max-w-md mx-auto relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={query}
            onChange={e => { setQuery(e.target.value); setShown(PAGE_SIZE); }}
            placeholder="Search name or country…"
            data-testid="founders-search"
            className="pl-9 bg-white/5 border-white/10 focus-visible:ring-fuchsia-500/40"
          />
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="h-40 rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50" data-testid="founders-empty">
            No founders match &ldquo;{query}&rdquo;. Try a different name or country.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="founders-grid">
              {visible.map(u => <Card key={u.user_id} user={u} />)}
            </div>

            {hasMore && (
              <div className="text-center mt-8">
                <Button
                  onClick={() => setShown(s => s + PAGE_SIZE)}
                  variant="outline"
                  data-testid="founders-load-more"
                  className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
                >
                  Load more ({filtered.length - shown} remaining)
                </Button>
              </div>
            )}

            <div className="text-center text-xs text-white/40 mt-6">
              Showing {visible.length} of {filtered.length}{query ? ' matching' : ''} founders
            </div>
          </>
        )}
      </section>

      {/* Footer CTA — Sign in */}
      <section className="max-w-3xl mx-auto px-4 mt-16 text-center">
        <div className="rounded-3xl border border-cyan-400/25 bg-gradient-to-br from-cyan-500/10 via-fuchsia-500/10 to-transparent p-8">
          <h2 className="font-display font-bold text-2xl">Already a traveler?</h2>
          <p className="mt-2 text-sm text-white/70">
            Sign in to check your rank, message founders and pick up where you left off.
          </p>
          <Link to="/login" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-black font-semibold hover:opacity-90 shadow-lg shadow-cyan-500/20" data-testid="founders-footer-signin">
            <LogIn size={16} /> Sign in
          </Link>
        </div>
      </section>
    </div>
  );
}
